import { useState, useEffect, useCallback, useRef } from "react";
import useEmblaCarousel from "embla-carousel-react";
import { ChevronLeft, ChevronRight, Play } from "lucide-react";

interface Video {
  src: string;
  title: string;
}

interface VideoCarouselProps {
  videos: Video[];
  autoplayDelay?: number;
}

// Extract YouTube video ID from embed URL
const getYouTubeId = (url: string): string => {
  const match = url.match(/embed\/([^?]+)/);
  return match ? match[1] : "";
};

const buildYouTubeEmbedSrc = (baseSrc: string, params: Record<string, string>) => {
  try {
    const u = new URL(baseSrc);
    Object.entries(params).forEach(([k, v]) => u.searchParams.set(k, v));
    return u.toString();
  } catch {
    // Fallback if it's not a full URL
    const joiner = baseSrc.includes("?") ? "&" : "?";
    const query = new URLSearchParams(params).toString();
    return `${baseSrc}${joiner}${query}`;
  }
};

const VideoCarousel = ({ videos, autoplayDelay = 4000 }: VideoCarouselProps) => {
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [playingIndex, setPlayingIndex] = useState<number | null>(null);
  const selectedIndexRef = useRef(0);
  const autoplayRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pauseUntilRef = useRef<number>(0);

  const [emblaRef, emblaApi] = useEmblaCarousel({ loop: true, dragFree: false });

  const onSelect = useCallback(() => {
    if (!emblaApi) return;

    const nextIndex = emblaApi.selectedScrollSnap();
    const didChangeSlide = nextIndex !== selectedIndexRef.current;

    selectedIndexRef.current = nextIndex;
    setSelectedIndex(nextIndex);

    // Only stop playback when the carousel actually changes slides.
    // Embla can emit select during pointer interactions even if the snap doesn't change.
    if (didChangeSlide) {
      setPlayingIndex(null);
    }
  }, [emblaApi]);

  const startAutoplay = useCallback(() => {
    if (autoplayRef.current) {
      clearInterval(autoplayRef.current);
    }
    
    autoplayRef.current = setInterval(() => {
      if (Date.now() < pauseUntilRef.current) return;
      if (playingIndex !== null) return; // Don't auto-scroll while video is playing
      if (!emblaApi) return;
      emblaApi.scrollNext();
    }, autoplayDelay);
  }, [emblaApi, autoplayDelay, playingIndex]);

  const pauseAutoplay = useCallback((duration: number = 10000) => {
    pauseUntilRef.current = Date.now() + duration;
  }, []);

  const handlePlayVideo = useCallback((index: number) => {
    setPlayingIndex(index);
    pauseUntilRef.current = Infinity; // Stop autoplay while playing
  }, []);

  const scrollPrev = useCallback(() => {
    if (emblaApi) {
      emblaApi.scrollPrev();
      pauseAutoplay(10000);
    }
  }, [emblaApi, pauseAutoplay]);

  const scrollNext = useCallback(() => {
    if (emblaApi) {
      emblaApi.scrollNext();
      pauseAutoplay(10000);
    }
  }, [emblaApi, pauseAutoplay]);

  useEffect(() => {
    if (!emblaApi) return;
    
    onSelect();
    emblaApi.on("select", onSelect);
    emblaApi.on("pointerDown", () => pauseAutoplay(10000));
    
    startAutoplay();
    
    return () => {
      emblaApi.off("select", onSelect);
      if (autoplayRef.current) {
        clearInterval(autoplayRef.current);
      }
    };
  }, [emblaApi, onSelect, startAutoplay, pauseAutoplay]);

  const scrollTo = useCallback(
    (index: number) => {
      if (emblaApi) {
        emblaApi.scrollTo(index);
        pauseAutoplay(10000);
      }
    },
    [emblaApi, pauseAutoplay]
  );

  return (
    <div className="relative group">
      <div className="overflow-hidden rounded-lg shadow-lg" ref={emblaRef}>
        <div className="flex">
          {videos.map((video, index) => {
            const videoId = getYouTubeId(video.src);
            const isPlaying = playingIndex === index;
            
            return (
              <div
                key={index}
                className="relative flex-[0_0_100%] min-w-0"
              >
                <div className="relative aspect-video w-full bg-black">
                  {isPlaying ? (
                    // Show iframe with autoplay when playing
                    <iframe
                      className="w-full h-full"
                      src={buildYouTubeEmbedSrc(video.src, { rel: "0", autoplay: "1" })}
                      title={video.title}
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                      allowFullScreen
                    />
                  ) : (
                    // Show thumbnail with play button - allows swiping
                    <div 
                      className="w-full h-full cursor-pointer"
                      onClick={() => handlePlayVideo(index)}
                    >
                      <img
                        src={`https://img.youtube.com/vi/${videoId}/maxresdefault.jpg`}
                        alt={video.title}
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          // Fallback to hqdefault if maxresdefault doesn't exist
                          (e.target as HTMLImageElement).src = `https://img.youtube.com/vi/${videoId}/hqdefault.jpg`;
                        }}
                      />
                      {/* Play button overlay */}
                      <div className="absolute inset-0 flex items-center justify-center bg-black/20 hover:bg-black/30 transition-colors">
                        <div className="w-16 h-16 md:w-20 md:h-20 bg-primary/90 hover:bg-primary rounded-full flex items-center justify-center transition-colors shadow-lg">
                          <Play className="w-8 h-8 md:w-10 md:h-10 text-primary-foreground ml-1" fill="currentColor" />
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Arrow Navigation */}
      <button
        onClick={scrollPrev}
        className="absolute left-2 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white p-2 rounded-full opacity-0 group-hover:opacity-100 transition-opacity z-10"
        aria-label="Previous video"
      >
        <ChevronLeft className="w-5 h-5" />
      </button>
      <button
        onClick={scrollNext}
        className="absolute right-2 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white p-2 rounded-full opacity-0 group-hover:opacity-100 transition-opacity z-10"
        aria-label="Next video"
      >
        <ChevronRight className="w-5 h-5" />
      </button>

      {/* Navigation Dots */}
      <div className="flex justify-center gap-2 mt-4">
        {videos.map((_, index) => (
          <button
            key={index}
            onClick={() => scrollTo(index)}
            className={`w-2.5 h-2.5 rounded-full transition-all duration-300 ${
              index === selectedIndex
                ? "bg-primary w-6"
                : "bg-secondary/30 hover:bg-secondary/50"
            }`}
            aria-label={`Go to video ${index + 1}`}
          />
        ))}
      </div>
    </div>
  );
};

export default VideoCarousel;
